import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { z } from 'zod';

import { AutodocViewer } from '@/components/Autodocs/AutodocViewer';
import { DocsSidebar } from '@/components/CodebaseView/DocsSidebar';
import { LeftSidebarPortal } from '@/components/portals/LeftSidebarPortal';
import { TocProvider, useToc } from '@/contexts/TocContext';
import { useVersions } from '@/contexts/VersionsContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useCodebaseRoute } from '@/hooks/useCodebaseRoute';
import { searchParam } from '@/utils/router';

const autodocSearchSchema = z.object({
  branchId: searchParam().optional(),
  versionId: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/cb/$codebaseId/autodocs/$autodocId')({
  component: ProvidedAutodocsDetailPage,
  validateSearch: zodValidator(autodocSearchSchema),
});

function ProvidedAutodocsDetailPage() {
  const { captureEvent } = useAnalytics();
  const { autodocId } = Route.useParams();
  const { codebaseId } = useCodebaseRoute();
  const { rootVersionNodeId } = useVersions();

  useEffect(() => {
    captureEvent('autodoc_viewed', { codebase_id: codebaseId, autodoc_id: autodocId });
  }, [captureEvent, codebaseId, autodocId]);

  return (
    // We need to change the key to the autodocId to force a reset of the toc when the autodocId changes.
    <TocProvider key={autodocId}>
      <div className="mx-auto flex w-full flex-col gap-y-6 pt-4 pb-4">
        <AutodocsDetailPage autodocId={autodocId} versionNodeId={rootVersionNodeId} />
      </div>
    </TocProvider>
  );
}

interface AutodocsDetailPageProps {
  autodocId: string;
  versionNodeId: string | undefined;
}

function AutodocsDetailPage({ autodocId, versionNodeId }: AutodocsDetailPageProps) {
  const { codebaseId, organizationName } = useCodebaseRoute();
  const { branchId, versionId } = Route.useSearch();
  const { editor, handleTabClick, tocItems } = useToc();

  return (
    <>
      <LeftSidebarPortal.Mount>
        <DocsSidebar
          backTo="/$organizationName/cb/$codebaseId/autodocs"
          backParams={{
            organizationName,
            codebaseId,
          }}
          backSearch={
            branchId || versionId
              ? { ...(branchId ? { branchId } : {}), ...(versionId ? { versionId } : {}) }
              : undefined
          }
          tocItems={tocItems}
          onTocClick={handleTabClick}
          editor={editor}
          showExpandCollapseButton={false}
        />
      </LeftSidebarPortal.Mount>
      <AutodocViewer autodocId={autodocId} versionNodeId={versionNodeId} />
    </>
  );
}
